import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { ArrowLeft, Eye, EyeOff, KeyRound, CheckCircle2, AlertCircle } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_46ba8d0f-50d1-45b0-932d-164e40ecbfdf/artifacts/l81b46nl_K2B_Logo_Final-2.png";

export default function ResetPasswordPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);
    const [validating, setValidating] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [tokenEmail, setTokenEmail] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        api.get(`/auth/reset-password/${token}/validate`)
            .then(({ data }) => {
                setTokenValid(true);
                setTokenEmail(data.email || "");
            })
            .catch((err) => {
                setError(formatApiError(err.response?.data?.detail) || "Invalid or expired reset link");
            })
            .finally(() => setValidating(false));
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 6) {
            setError("Password must be at least 6 characters");
            return;
        }
        if (password !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, password });
            setSuccess(true);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    if (validating) return (
        <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
        </div>
    );

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
            <Card className="w-full max-w-md border-border bg-card" data-testid="reset-password-card">
                <CardHeader className="space-y-4 text-center pb-2">
                    <div className="flex justify-center mb-2">
                        <img src={LOGO_URL} alt="K2B Learning" className="h-14" />
                    </div>
                    {success ? (
                        <>
                            <div className="flex justify-center">
                                <div className="p-4 rounded-full" style={{ backgroundColor: "#5BA88E20" }}>
                                    <CheckCircle2 className="h-10 w-10" style={{ color: "#5BA88E" }} />
                                </div>
                            </div>
                            <h2 className="text-2xl font-semibold text-foreground" style={{ fontFamily: "Outfit" }}>
                                Password Reset!
                            </h2>
                            <p className="text-sm text-muted-foreground">
                                Your password has been updated successfully.
                            </p>
                        </>
                    ) : !tokenValid ? (
                        <>
                            <div className="flex justify-center">
                                <div className="p-4 rounded-full bg-destructive/10">
                                    <AlertCircle className="h-10 w-10 text-destructive" />
                                </div>
                            </div>
                            <h2 className="text-2xl font-semibold text-foreground" style={{ fontFamily: "Outfit" }}>
                                Invalid Link
                            </h2>
                            <p className="text-sm text-muted-foreground">{error}</p>
                        </>
                    ) : (
                        <>
                            <h2 className="text-2xl font-semibold text-foreground" style={{ fontFamily: "Outfit" }}>
                                Set New Password
                            </h2>
                            <p className="text-sm text-muted-foreground">
                                Enter a new password for <span className="text-foreground font-medium">{tokenEmail}</span>
                            </p>
                        </>
                    )}
                </CardHeader>
                <CardContent>
                    {success ? (
                        <Button
                            data-testid="go-to-login-btn"
                            className="w-full gap-2"
                            style={{ backgroundColor: "#4A7AB5" }}
                            onClick={() => navigate("/login")}
                        >
                            <KeyRound className="h-4 w-4" /> Sign In Now
                        </Button>
                    ) : !tokenValid ? (
                        <div className="space-y-3">
                            <Link to="/forgot-password">
                                <Button data-testid="request-new-link-btn" className="w-full" style={{ backgroundColor: "#4A7AB5" }}>
                                    Request a New Link
                                </Button>
                            </Link>
                            <Link to="/login">
                                <Button variant="ghost" className="w-full gap-2 text-muted-foreground mt-2">
                                    <ArrowLeft className="h-4 w-4" /> Back to Sign In
                                </Button>
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-5">
                            {error && (
                                <div data-testid="reset-error" className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-sm text-destructive">
                                    {error}
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-sm text-muted-foreground">New Password</Label>
                                <div className="relative">
                                    <Input
                                        data-testid="reset-password-input"
                                        id="password"
                                        type={showPw ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="At least 6 characters"
                                        className="bg-background border-border pr-10"
                                        required
                                    />
                                    <button type="button" onClick={() => setShowPw(!showPw)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                                        {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="confirm" className="text-sm text-muted-foreground">Confirm Password</Label>
                                <Input
                                    data-testid="reset-confirm-input"
                                    id="confirm"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Repeat your new password"
                                    className="bg-background border-border"
                                    required
                                />
                            </div>
                            <Button
                                data-testid="reset-submit-btn"
                                type="submit"
                                className="w-full gap-2"
                                disabled={loading}
                                style={{ backgroundColor: "#4A7AB5" }}
                            >
                                {loading ? (
                                    <div className="flex items-center gap-2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                        Resetting...
                                    </div>
                                ) : (
                                    <>
                                        <KeyRound className="h-4 w-4" /> Reset Password
                                    </>
                                )}
                            </Button>
                        </form>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
